<script lang="ts" setup>
import { computed, resolveComponent } from 'vue'
import type { NavItemVariant } from './types'

interface Props {
  active?: boolean
  href?: string
  to?: string | object
  componentClass?: string
  variant?: keyof typeof NavItemVariant
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
})

const tag = computed(() => {
  if (props.href)
    return 'a'

  if (props.to)
    return 'nuxt-link'

  return 'button'
})

const computedComponent = computed(() => {
  if (tag.value === 'nuxt-link')
    return resolveComponent('nuxt-link')

  return tag.value
})

const bound = computed(() => {
  let b
  switch (tag.value) {
    case 'a':
      b = { href: props.href }
      break
    case 'nuxt-link':
      b = { to: props.to }
      break
  }
  return b
})

const classes = computed(() => {
  const cls = ['flex items-center gap-3 rounded-lg py-1.5 px-3 text-sm grow']

  if (props.active) {
    if (props.variant === 'light-blue')
      cls.push('bg-blue-50 hover:bg-blue-50 text-blue-800')
    else
      cls.push('bg-gray-100 hover:bg-gray-100 text-gray-800')
  }
  else {
    cls.push('text-gray-700')
    if (props.variant === 'light-blue')
      cls.push('hover:bg-blue-25')
    else
      cls.push('hover:bg-gray-50')
  }

  if (tag.value === 'button')
    cls.push('cursor-default')

  else
    cls.push('cursor-pointer no-underline hover:no-underline')

  if (props.componentClass)
    cls.push(props.componentClass)

  return cls
})
</script>

<template>
  <li class="flex list-none">
    <component
      :is="computedComponent"
      v-bind="bound"
      :class="classes"
    >
      <slot />
    </component>
  </li>
</template>
